import { FONT, PAPER_SIZE } from '../../types/enums';
import pdfStyles from './pdfDocument/pdfStyles';
import ReactPDF from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import LanguageHelper from '../../common/helpers/language/LanguageHelper';
import { GetFontFamilyOptions } from '../../utils/types';

export function combinePdfStyles<T extends Object>(value: T, newValue: T): T {
  return { ...value, ...newValue };
}

export const getPdfStyles = (paperSize: PAPER_SIZE): ReactPDF.Styles => {
  if (paperSize === PAPER_SIZE.LETTER) {
    return {
      ...pdfStyles,
      nameView: combinePdfStyles<Style>(pdfStyles.nameView, pdfStyles.letterNameView)
    };
  }
  if (paperSize === PAPER_SIZE.W105xH140mm) {
    return {
      ...pdfStyles,
      name: combinePdfStyles<Style>(pdfStyles.name, { marginBottom: 0 }),
      company: combinePdfStyles<Style>(pdfStyles.company, { minHeight: 0, marginTop: 0 }),
      badgeItem: combinePdfStyles<Style>(pdfStyles.badgeItem, { justifyContent: 'flex-end', paddingTop: '40mm', paddingBottom: '24' }),
      customerTypeWrapper: combinePdfStyles<Style>(pdfStyles.customerTypeWrapper, { paddingTop: 10, marginTop: 10 }),
      threeIconContainer: combinePdfStyles<Style>(pdfStyles.threeIconContainer, { top: '86%' }),
      twoIconContainer: combinePdfStyles<Style>(pdfStyles.threeIconContainer, { top: '100%' }),
      oneIconContainer: combinePdfStyles<Style>(pdfStyles.threeIconContainer, { top: '115%' })
    };
  }
  return pdfStyles;
};

const getStylesByJobTitleLength = (pdfStyles: ReactPDF.Styles, jobTitleLength: number) => {
  if (jobTitleLength <= 40) {
    pdfStyles.company =
      combinePdfStyles<Style>(pdfStyles.company, { marginTop: 24 });
  } else {
    pdfStyles.company =
      combinePdfStyles<Style>(pdfStyles.company, { marginTop: 22 });
  }
  return pdfStyles;
};

const getCommonStyles = (
  pdfStylesInit: ReactPDF.Styles,
  isSingleLineName: boolean,
  jobTitleLength: number,
  paperSize: PAPER_SIZE
) => {
  let pdfStyles = pdfStylesInit;
  if (!isSingleLineName) {
    pdfStyles.address =
      combinePdfStyles<Style>(pdfStyles.address, {
        paddingTop: pdfStyles.addressWithReducedPaddingTop.paddingTop
      });
  } else if(paperSize === PAPER_SIZE.LETTER) {
    pdfStyles.address = combinePdfStyles<Style>(pdfStyles.address, {
      paddingTop: pdfStyles.addressWithReducedPaddingTop.paddingTop
    });
    pdfStyles.qrCode = combinePdfStyles<Style>(pdfStyles.qrCode, {
      marginTop: 0
    });
  }
  pdfStyles = combinePdfStyles<ReactPDF.Styles>(pdfStyles, getStylesByJobTitleLength(pdfStyles, jobTitleLength));

  return pdfStyles;
};

const getW105mmH140mmStyles = (
  pdfStylesInit: ReactPDF.Styles,
  isSingleLineName: boolean
) => {
  const pdfStyles = pdfStylesInit;
  if (isSingleLineName) {
    pdfStyles.company =
      combinePdfStyles<Style>(pdfStyles.company, { marginTop: 20, marginBottom: 10 });
    pdfStyles.address = combinePdfStyles<Style>(pdfStyles.address, { paddingBottom: 10 });
  } else {
    pdfStyles.company =
      combinePdfStyles<Style>(pdfStyles.company, { marginTop: 15, marginBottom: 5 });
    pdfStyles.eventLogo =
      combinePdfStyles<Style>(pdfStyles.eventLogo, { minHeight: 0 });
  }

  return pdfStyles;
};

export const updatePdfStyles = (
  pdfStyles: ReactPDF.Styles,
  paperSize: PAPER_SIZE,
  isSingleLineName: boolean,
  jobTitleLength: number
) => {
  if (paperSize === PAPER_SIZE.W105xH140mm) {
    return combinePdfStyles<ReactPDF.Styles>(pdfStyles, getW105mmH140mmStyles(pdfStyles, isSingleLineName));
  } else {
    return combinePdfStyles<ReactPDF.Styles>(pdfStyles, getCommonStyles(pdfStyles, isSingleLineName, jobTitleLength, paperSize));
  }
};

export const getFontFamily = (value: string, { fallbackFont, bold}: GetFontFamilyOptions = {fallbackFont: undefined, bold: false}) => {
  if (LanguageHelper.containsTurkish(value) || LanguageHelper.containsPolish(value) || LanguageHelper.containsDeutch(value)) {
    return { fontFamily: FONT.NOTO_SANS_TMP };
  }
  if (LanguageHelper.isLocaleChineseSimplified() && !LanguageHelper.containsJapaneseChars(value)) {
    return { fontFamily: FONT.CHINESE_SIMPLIFIED };
  }
  if (LanguageHelper.containsChineseOrJapaneseChars(value) && !bold) {
    return { fontFamily: FONT.JAPANESE };
  }
  if (LanguageHelper.containsChineseOrJapaneseChars(value) && bold) {
    return { fontFamily: FONT.JAPANESE_BOLD_800 };
  }
  if (LanguageHelper.containsKoreanChars(value)) {
    return { fontFamily: FONT.NOTO_SANS };
  }
  if (fallbackFont) {
    return { fontFamily: fallbackFont };
  }
  if (bold) {
    return { fontFamily: FONT.HELVETICA_BOLD };
  }
  return { fontFamily: FONT.HELVETICA };
};
